import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Building2, Sparkles, Ticket } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLang } from '@/lib/i18n';
import { api, errMsg } from '@/lib/api';
import { AppShell } from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const INDUSTRIES = ['Retail', 'F&B', 'Jasa Profesional', 'Manufaktur', 'Teknologi', 'Pendidikan', 'Kesehatan', 'Properti', 'Pariwisata', 'Lainnya'];

export default function OnboardingPage() {
  const { refreshMe, switchCompany, companies, user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: '', industry: '', phone: '', address: '', voucher_code: '' });
  const [busy, setBusy] = useState(false);
  const [promo, setPromo] = useState(null); // {ok, msg, label}
  const checkPromo = async () => {
    if (!form.voucher_code.trim()) { setPromo(null); return; }
    try { const { data } = await api.post('/promo/validate', { code: form.voucher_code, context: 'signup' }); setPromo({ ok: true, msg: `${data.voucher.label} — langganan langsung aktif tanpa uji coba`, label: data.voucher.label }); } catch (err) { setPromo({ ok: false, msg: errMsg(err) }); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post('/companies', form);
      await refreshMe();
      await switchCompany(data.id);
      toast.success(data.voucher ? `Workspace dibuat. Voucher ${data.voucher.code}: ${data.voucher.label} — langganan aktif!` : 'Workspace berhasil dibuat. Uji coba gratis 14 hari dimulai.');
      navigate('/apps');
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const body = (
    <div className="mx-auto max-w-xl px-4 py-10 md:py-16 fade-up">
      <div className="rounded-2xl border border-border bg-white p-8 shadow-soft">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-secondary mb-5"><Building2 className="text-primary" /></div>
        <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">{t('onboarding_title')}</h1>
        <p className="mt-1 text-muted-foreground">{t('onboarding_sub')}</p>
        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="onboarding-form">
          <div className="space-y-1.5">
            <Label>{t('company_name')} *</Label>
            <Input required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="PT Maju Bersama" data-testid="onboarding-company-name-input" className="rounded-xl h-11" />
          </div>
          <div className="space-y-1.5">
            <Label>{t('industry')}</Label>
            <Select value={form.industry} onValueChange={(v) => setForm({ ...form, industry: v })}>
              <SelectTrigger className="rounded-xl h-11" data-testid="onboarding-industry-select"><SelectValue placeholder="Pilih industri" /></SelectTrigger>
              <SelectContent>{INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{t('phone')}</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+62" data-testid="onboarding-phone-input" className="rounded-xl h-11" />
            </div>
            <div className="space-y-1.5">
              <Label>{t('address')}</Label>
              <Textarea rows={1} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="onboarding-address-input" className="rounded-xl min-h-[44px]" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Punya kode promo?</Label>
            <div className="flex gap-2"><Input value={form.voucher_code} onChange={(e) => { setForm({ ...form, voucher_code: e.target.value.toUpperCase() }); setPromo(null); }} onBlur={checkPromo} placeholder="PROMO2026" className="rounded-xl h-11 font-mono uppercase" data-testid="onboarding-voucher-input" /><Button type="button" variant="outline" className="rounded-xl h-11" onClick={checkPromo} data-testid="onboarding-voucher-check"><Ticket size={14} className="mr-1" />Cek</Button></div>
            {promo && <p className={`text-xs ${promo.ok ? 'text-emerald-700' : 'text-rose-600'}`} data-testid="onboarding-voucher-result">{promo.msg}</p>}
          </div>
          <div className="rounded-xl bg-secondary/60 p-4 text-sm flex gap-3">
            <Sparkles className="text-[hsl(var(--pillot-mint))] shrink-0" size={18} />
            <p>Workspace baru otomatis mendapat <strong>uji coba gratis 14 hari</strong> dengan 3 seat pengguna. Modul Website & Pengaturan langsung aktif.</p>
          </div>
          <div className="flex gap-3">
            {user?.is_super_admin && companies.length === 0 && <Button type="button" variant="outline" className="rounded-xl h-11" onClick={() => navigate('/admin')} data-testid="onboarding-skip-admin">{t('super_admin')}</Button>}
            {companies.length > 0 && params.get('new') && <Button type="button" variant="outline" className="rounded-xl h-11" onClick={() => navigate('/apps')}>{t('cancel')}</Button>}
            <Button type="submit" disabled={busy} className="flex-1 h-11 rounded-xl" data-testid="onboarding-submit-button">
              {busy ? <Loader2 className="animate-spin" size={18} /> : t('create_workspace')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );

  return <AppShell className="bg-apps-wash">{body}</AppShell>;
}
